/**
 * Content Manager - Approvals Service
 *
 * API service for managing approval workflows.
 */

import type {
  ApprovalWorkflow,
  ApprovalStep,
  ApprovalDecision,
  WorkflowTemplate,
  ApiResponse,
} from '../types';
import { logger } from '../lib/logger';

// =============================================================================
// DEFAULT WORKFLOW TEMPLATES
// =============================================================================

const DEFAULT_WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'wft-simple',
    name: 'Simple Approval',
    description: 'Single-step approval by an editor or admin',
    steps: [
      {
        order: 1,
        name: 'Editorial Review',
        approverIds: [],
        approverRoles: ['Editor', 'Admin'],
        requiredApprovals: 1,
        dueDate: null,
      },
    ],
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'wft-two-step',
    name: 'Two-Step Approval',
    description: 'Editor review followed by final approval',
    steps: [
      {
        order: 1,
        name: 'Editorial Review',
        approverIds: [],
        approverRoles: ['Editor', 'Admin'],
        requiredApprovals: 1,
        dueDate: null,
      },
      {
        order: 2,
        name: 'Final Approval',
        approverIds: [],
        approverRoles: ['Admin'],
        requiredApprovals: 1,
        dueDate: null,
      },
    ],
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'wft-enterprise',
    name: 'Enterprise Approval',
    description: 'Multi-step workflow with legal and compliance review',
    steps: [
      {
        order: 1,
        name: 'Content Review',
        approverIds: [],
        approverRoles: ['Editor'],
        requiredApprovals: 1,
        dueDate: null,
      },
      {
        order: 2,
        name: 'Legal Review',
        approverIds: [],
        approverRoles: ['Admin'],
        requiredApprovals: 1,
        dueDate: null,
      },
      {
        order: 3,
        name: 'Final Sign-off',
        approverIds: [],
        approverRoles: ['Admin'],
        requiredApprovals: 1,
        dueDate: null,
      },
    ],
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
];

// =============================================================================
// SERVICE
// =============================================================================

class ApprovalsService {
  private workflows: ApprovalWorkflow[] = [];
  private templates: WorkflowTemplate[] = [...DEFAULT_WORKFLOW_TEMPLATES];

  /**
   * Get all workflows
   */
  async getAll(): Promise<ApiResponse<ApprovalWorkflow[]>> {
    try {
      logger.info('content', 'Fetching all workflows');
      return { data: this.workflows, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get workflows for a specific entity
   */
  async getByEntity(
    entityType: 'seed' | 'output',
    entityId: string
  ): Promise<ApiResponse<ApprovalWorkflow[]>> {
    try {
      const result = this.workflows.filter(
        (w) => w.entityType === entityType && w.entityId === entityId
      );
      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get a workflow by ID
   */
  async getById(id: string): Promise<ApiResponse<ApprovalWorkflow>> {
    try {
      const workflow = this.workflows.find((w) => w.id === id);
      if (!workflow) {
        return { data: null, error: new Error('Workflow not found') };
      }
      return { data: workflow, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Create a new workflow from a template
   */
  async createFromTemplate(
    templateId: string,
    entityType: 'seed' | 'output',
    entityId: string,
    entityTitle: string,
    createdBy: string
  ): Promise<ApiResponse<ApprovalWorkflow>> {
    try {
      const template = this.templates.find((t) => t.id === templateId);
      if (!template) {
        return { data: null, error: new Error('Template not found') };
      }

      logger.info('content', 'Creating workflow from template', {
        templateId,
        entityType,
        entityId,
      });

      const now = new Date().toISOString();
      const workflowId = `wf-${Date.now()}`;

      const steps: ApprovalStep[] = template.steps.map((step: Omit<ApprovalStep, 'id' | 'workflowId' | 'currentApprovals' | 'status' | 'createdAt' | 'updatedAt'>, index: number) => ({
        id: `step-${workflowId}-${index + 1}`,
        workflowId,
        order: step.order,
        name: step.name,
        approverIds: step.approverIds,
        approverRoles: step.approverRoles,
        requiredApprovals: step.requiredApprovals,
        currentApprovals: [],
        status: index === 0 ? 'pending' : 'pending',
        dueDate: step.dueDate,
        createdAt: now,
        updatedAt: now,
      }));

      const workflow: ApprovalWorkflow = {
        id: workflowId,
        name: template.name,
        description: template.description,
        entityType,
        entityId,
        entityTitle,
        steps,
        currentStepIndex: 0,
        status: 'pending',
        createdBy,
        createdAt: now,
        updatedAt: now,
        completedAt: null,
      };

      this.workflows.push(workflow);
      return { data: workflow, error: null };
    } catch (error) {
      logger.error('content', 'Failed to create workflow', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Submit a decision for a workflow step
   */
  async submitDecision(
    workflowId: string,
    stepId: string,
    userId: string,
    userName: string,
    decision: 'approved' | 'rejected' | 'changes_requested',
    comment: string | null
  ): Promise<ApiResponse<ApprovalWorkflow>> {
    try {
      const workflowIndex = this.workflows.findIndex((w) => w.id === workflowId);
      if (workflowIndex === -1) {
        return { data: null, error: new Error('Workflow not found') };
      }

      const workflow = this.workflows[workflowIndex];
      const stepIndex = workflow.steps.findIndex((s: ApprovalStep) => s.id === stepId);
      if (stepIndex === -1) {
        return { data: null, error: new Error('Step not found') };
      }

      const step = workflow.steps[stepIndex];

      // Check if user already submitted a decision
      const existingDecision = step.currentApprovals.find((a: ApprovalDecision) => a.userId === userId);
      if (existingDecision) {
        return { data: null, error: new Error('You have already submitted a decision') };
      }

      logger.info('content', 'Submitting approval decision', {
        workflowId,
        stepId,
        userId,
        decision,
      });

      // Add the decision
      const approvalDecision: ApprovalDecision = {
        id: `dec-${Date.now()}`,
        stepId,
        userId,
        userName,
        decision,
        comment,
        createdAt: new Date().toISOString(),
      };

      step.currentApprovals.push(approvalDecision);
      step.updatedAt = new Date().toISOString();

      // Determine step status
      if (decision === 'rejected') {
        step.status = 'rejected';
        workflow.status = 'rejected';
      } else if (decision === 'changes_requested') {
        step.status = 'changes_requested';
        workflow.status = 'changes_requested';
      } else {
        // Check if we have enough approvals
        const approvalCount = step.currentApprovals.filter((a: ApprovalDecision) => a.decision === 'approved').length;
        if (approvalCount >= step.requiredApprovals) {
          step.status = 'approved';

          // Move to next step or complete workflow
          if (stepIndex === workflow.steps.length - 1) {
            workflow.status = 'completed';
            workflow.completedAt = new Date().toISOString();
          } else {
            workflow.currentStepIndex = stepIndex + 1;
            workflow.steps[stepIndex + 1].status = 'pending';
          }
        }
      }

      workflow.updatedAt = new Date().toISOString();
      this.workflows[workflowIndex] = workflow;

      return { data: workflow, error: null };
    } catch (error) {
      logger.error('content', 'Failed to submit decision', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Cancel a workflow
   */
  async cancel(workflowId: string): Promise<ApiResponse<boolean>> {
    try {
      const index = this.workflows.findIndex((w) => w.id === workflowId);
      if (index === -1) {
        return { data: null, error: new Error('Workflow not found') };
      }

      this.workflows.splice(index, 1);
      logger.info('content', 'Cancelled workflow', { workflowId });
      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get pending approvals for a user
   */
  async getPendingForUser(userId: string, userRole: string): Promise<ApiResponse<ApprovalWorkflow[]>> {
    try {
      const pending = this.workflows.filter((w) => {
        if (w.status !== 'pending') return false;
        const currentStep = w.steps[w.currentStepIndex];
        if (!currentStep) return false;

        // Check if user is an approver
        const isApprover =
          currentStep.approverIds.includes(userId) ||
          currentStep.approverRoles.includes(userRole as any);

        // Check if user hasn't already approved
        const hasApproved = currentStep.currentApprovals.some((a: ApprovalDecision) => a.userId === userId);

        return isApprover && !hasApproved;
      });

      return { data: pending, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get workflow templates
   */
  async getTemplates(): Promise<ApiResponse<WorkflowTemplate[]>> {
    try {
      return { data: this.templates, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Create a custom workflow template
   */
  async createTemplate(
    template: Omit<WorkflowTemplate, 'id' | 'createdAt'>
  ): Promise<ApiResponse<WorkflowTemplate>> {
    try {
      const newTemplate: WorkflowTemplate = {
        ...template,
        id: `wft-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };

      this.templates.push(newTemplate);
      return { data: newTemplate, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get workflow statistics
   */
  getStats(): {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    averageCompletionTime: number;
  } {
    const completed = this.workflows.filter((w) => w.status === 'completed');
    let avgTime = 0;

    if (completed.length > 0) {
      const totalTime = completed.reduce((acc, w) => {
        if (w.completedAt) {
          return acc + (new Date(w.completedAt).getTime() - new Date(w.createdAt).getTime());
        }
        return acc;
      }, 0);
      avgTime = totalTime / completed.length / (1000 * 60 * 60); // Convert to hours
    }

    return {
      total: this.workflows.length,
      pending: this.workflows.filter((w) => w.status === 'pending').length,
      approved: completed.length,
      rejected: this.workflows.filter((w) => w.status === 'rejected').length,
      averageCompletionTime: Math.round(avgTime * 10) / 10,
    };
  }
}

export const approvalsService = new ApprovalsService();
export default approvalsService;
